import { PartnerResponse } from "./partner";
import { ProductResponse } from "./product";
import { UnitResponse } from "./unit";

export interface PreOrderFlowBase {
  product_id: string | null;
  partner_id: string | null;
  quantity: number;
  unit_id: string | null;
  weight: number | null;
  price_per_weight: number | null;
  comment: string | null;
  hidden_comment: string | null;
  status: number;
  index: number | null;
  delivery_date: string | null;
  order_date: string | null;
  flow_metadata: Record<string, any> | null;
}

export interface PreOrderFlowCreate extends PreOrderFlowBase {
  user_id?: string | null;
  company_id?: string;
  pre_order_id: string;
}

export interface PreOrderFlowUpdate {
  product_id?: string | null;
  partner_id?: string | null;
  quantity?: number;
  unit_id?: string | null;
  weight?: number | null;
  price_per_weight?: number | null;
  comment?: string | null;
  hidden_comment?: string | null;
  status?: number;
  index?: number | null;
  delivery_date?: string | null;
  order_date?: string | null;
  flow_metadata?: Record<string, any> | null;
}

export interface PreOrderFlowResponse extends PreOrderFlowBase {
  id: string;
  user_id: string | null;
  company_id: string;
  pre_order_id: string;
  created_at: string;
  updated_at: string;
}

export interface PreOrderFlowWithProduct extends PreOrderFlowResponse {
  product: ProductResponse | null;
  partner: PartnerResponse | null;
  unit: UnitResponse | null;
  /**
   * Echoed by the backend on flows that were created within a
   * modify-pending / modify-confirmed request. Equals the client-supplied
   * `temp_id` from the matching `flow_additions[]` entry. `null` for
   * pre-existing flows. Used by the modify mutation hooks to locate the
   * locally-buffered (enriched) copy of the new flow so its
   * product/unit/partner survive the cache merge.
   */
  temp_id?: string | null;
}

export interface StockHistoryFlowResponse extends PreOrderFlowWithProduct {
  employee_name: string | null;
  resolved_partner_id: string | null;
  resolved_employee_id: string | null;
  resolved_order_date: string | null;
  resolved_delivery_date: string | null;
}

export enum PreOrderFlowStatus {
  PENDING = 0,
  CONFIRMED = 1,
  FLAGGED = 2,
  CANCELLED = 3,
  CORRECTED = 4,
  /**
   * Whole-line AI proposal: this flow was proposed by AI and has not yet
   * been accepted or declined by the user. Rendered with strong violet
   * styling (dashed outline + bg) and surfaces accept/decline controls.
   * Persisted in the DB by the AI service; cleared/promoted when the user
   * accepts (status → PENDING) or declines (row removed).
   */
  AI_PROPOSAL = 5,
}

/**
 * AI-staged change attached to a flow via `flow_metadata.ai_suggestions[]`.
 * Append-only indexed array of audit entries (latest last).
 *
 * - `type === "add"` is conceptually equivalent to `flow.status === AI_PROPOSAL`
 *   (whole-line proposal). Backends MAY emit either form; the UI accepts both.
 * - `type === "modify"` ships per-field `{ from, to, finalTo? }` diffs.
 * - `type === "delete"` proposes removing the row.
 *
 * `status === "pending"` is the actionable state; `accepted` / `declined` /
 * `modified` entries are part of the durable audit trail and stay in the
 * array. `modified` is set when the user accepts but overrides the AI's
 * `to` value with their own (`finalTo`).
 *
 * Re-exported from `@stockline/shared` so the inline recap cells and the
 * `/ai-pre-orders` approval queue read the same shape.
 */
export type {
  AiFieldChange as AiSuggestionFieldChange,
  AiSuggestionBlock as AiSuggestion,
  AiSuggestionsArray,
} from "@stockline/shared/validation/ai/pre-order";

export function getFlowStatusString(status: number): string {
  switch (status) {
    case PreOrderFlowStatus.PENDING:
      return "pending";
    case PreOrderFlowStatus.CONFIRMED:
      return "confirmed";
    case PreOrderFlowStatus.FLAGGED:
      return "flagged";
    case PreOrderFlowStatus.CANCELLED:
      return "cancelled";
    case PreOrderFlowStatus.CORRECTED:
      return "corrected";
    case PreOrderFlowStatus.AI_PROPOSAL:
      return "ai_proposal";
    default:
      return "unknown";
  }
}

export function getFlowStatusFromString(status: string): number {
  switch (status.toLowerCase()) {
    case "pending":
      return PreOrderFlowStatus.PENDING;
    case "confirmed":
      return PreOrderFlowStatus.CONFIRMED;
    case "flagged":
      return PreOrderFlowStatus.FLAGGED;
    case "cancelled":
      return PreOrderFlowStatus.CANCELLED;
    case "corrected":
      return PreOrderFlowStatus.CORRECTED;
    case "ai_proposal":
      return PreOrderFlowStatus.AI_PROPOSAL;
    default:
      return PreOrderFlowStatus.PENDING;
  }
}
